// Bounded-concurrency async map.
//
// Why this exists: the admin-action n8n workflow (workflow 11) runs on the
// n8n Cloud Starter plan, which has a low production-execution concurrency
// cap. Firing one webhook per selected ticket ALL AT ONCE (Promise.all over
// 30 selected tickets) floods that queue; n8n Cloud then auto-deactivates the
// workflow to protect the instance, which surfaces to users as
// "404 Active version not found for workflow …" and a pile of "crashed"
// executions. Capping the in-flight count keeps bulk actions under the limit.
//
// Mirrors Promise.allSettled's result shape so callers can count failures.
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let next = 0;
  const lanes = Math.max(1, Math.min(limit, items.length));

  async function worker() {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      try {
        results[i] = { status: 'fulfilled', value: await fn(items[i], i) };
      } catch (reason) {
        results[i] = { status: 'rejected', reason };
      }
    }
  }

  await Promise.all(Array.from({ length: lanes }, () => worker()));
  return results;
}
